/**
 * S2S (Service-to-Service) authentication configuration.
 * Single source of truth for all S2S endpoints that bypass user authentication.
 *
 * @description
 * S2S endpoints are used for service-to-service communication where
 * traditional user authentication is not applicable. These endpoints
 * require special handling on the server side.
 */

import {
    SERVICE_NAMES, PROXY_PREFIXES, API_ENDPOINTS, type ServiceName
} from '../constants';

/**
 * Configuration for a single S2S endpoint
 */
export interface S2SEndpointConfig {
    service: ServiceName;
    path: string;
    method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
    description: string;
    shouldSetRefreshToken?: boolean;
    /**
     * When true, the trusted device cookie is forwarded to the microservice as `trusted_device_token`;
     * a client supplied `trusted_device_token` in the body is always dropped, on every endpoint.
     */
    shouldForwardTrustedDeviceToken?: boolean;
    /**
     * When true, a `trusted_device_token` in the microservice response is
     * moved into an httpOnly cookie and stripped from the response payload.
     */
    shouldSetTrustedDeviceCookie?: boolean;
    /**
     * When true, the endpoint is NOT exposed as a public `/s2s/*` HTTP route
     * by the auto-mounting router. Server-side handlers may still call it via
     * `useS2SAuth: true` (token is attached directly inside Node). Client-side
     * attempts throw at `applyS2SAuth`.
     *
     * Use for lookups that would leak PII if auto-mounted (e.g. user by email,
     * which would otherwise allow email existence enumeration from the browser).
     */
    isServerOnly?: boolean;
}

/**
 * Registry of all allowed S2S endpoints.
 * Add new endpoints here when they need to bypass user authentication.
 */
export const S2S_AUTH_ENDPOINTS = {
    USER_FORGOT_PASSWORD: {
        service: SERVICE_NAMES.USER,
        path: API_ENDPOINTS[SERVICE_NAMES.USER].FORGOT_PASSWORD,
        method: 'POST' as const,
        description: 'Send password reset email without user context'
    },
    USER_RESET_PASSWORD: {
        service: SERVICE_NAMES.USER,
        path: API_ENDPOINTS[SERVICE_NAMES.USER].RESET_PASSWORD,
        method: 'PATCH' as const,
        description: 'Reset user password with token'
    },
    USER_SECURITY_POLICY_BY_ID: {
        service: SERVICE_NAMES.CONFIGURATION,
        path: API_ENDPOINTS[SERVICE_NAMES.CONFIGURATION].USER_SECURITY_POLICY_BY_ID,
        method: 'GET' as const,
        description: 'Get security policy for specific user without auth'
    },
    USER_BY_EMAIL: {
        service: SERVICE_NAMES.USER,
        path: API_ENDPOINTS[SERVICE_NAMES.USER].BY_EMAIL,
        method: 'GET' as const,
        description: 'Look up user by email during SSO pre-flight (server-only, see senderFederatedIdentityHandler)',
        isServerOnly: true
    },
    USER_TOKEN_FEATURES_BY_ID: {
        service: SERVICE_NAMES.USER,
        path: API_ENDPOINTS[SERVICE_NAMES.USER].TOKEN_FEATURES_BY_ID,
        method: 'GET' as const,
        description: 'Get token features for a user during SSO pre-flight'
    },
    FEATURE_ACCOUNT_FEATURES: {
        service: SERVICE_NAMES.FEATURE,
        path: API_ENDPOINTS[SERVICE_NAMES.FEATURE].ACCOUNT_FEATURES,
        method: 'GET' as const,
        description: 'Get feature flags for an account during SSO pre-flight'
    },
    AUTHORIZE_LEGACY: {
        service: SERVICE_NAMES.AUTHORIZATION_TOKEN,
        path: API_ENDPOINTS[SERVICE_NAMES.AUTHORIZATION_TOKEN].AUTHORIZE_LEGACY,
        method: 'POST' as const,
        description: 'Authorize recipient using legacy loginToken'
    },
    AUTHORIZE_SSO: {
        service: SERVICE_NAMES.AUTHORIZATION_TOKEN,
        path: API_ENDPOINTS[SERVICE_NAMES.AUTHORIZATION_TOKEN].AUTHORIZE_SSO,
        method: 'POST' as const,
        description: 'Exchange authorization code for OAuth tokens (SSO flow)'
    },
    // Registration endpoints (user not authenticated yet)
    REGISTRATION_VALIDATE_EMAIL: {
        service: SERVICE_NAMES.REGISTRATION,
        path: API_ENDPOINTS[SERVICE_NAMES.REGISTRATION].VALIDATE_EMAIL,
        method: 'POST' as const,
        description: 'Validate email for new account registration'
    },
    REGISTRATION_VALIDATE_PHONE: {
        service: SERVICE_NAMES.REGISTRATION,
        path: API_ENDPOINTS[SERVICE_NAMES.REGISTRATION].VALIDATE_PHONE,
        method: 'POST' as const,
        description: 'Validate phone number for new account registration'
    },
    REGISTRATION_CREATE: {
        service: SERVICE_NAMES.REGISTRATION,
        path: API_ENDPOINTS[SERVICE_NAMES.REGISTRATION].CREATE,
        method: 'POST' as const,
        description: 'Create new account registration'
    },
    REGISTRATION_STATUS: {
        service: SERVICE_NAMES.REGISTRATION,
        path: API_ENDPOINTS[SERVICE_NAMES.REGISTRATION].STATUS,
        method: 'GET' as const,
        description: 'Get registration status by ID'
    },
    REGISTRATION_VERIFY_EMAIL: {
        service: SERVICE_NAMES.REGISTRATION,
        path: API_ENDPOINTS[SERVICE_NAMES.REGISTRATION].VERIFY_EMAIL,
        method: 'POST' as const,
        description: 'Verify email address for registration'
    },
    REGISTRATION_VERIFY_SMS: {
        service: SERVICE_NAMES.REGISTRATION,
        path: API_ENDPOINTS[SERVICE_NAMES.REGISTRATION].VERIFY_SMS,
        method: 'POST' as const,
        description: 'Verify SMS code for registration'
    },
    REGISTRATION_RESEND_SMS: {
        service: SERVICE_NAMES.REGISTRATION,
        path: API_ENDPOINTS[SERVICE_NAMES.REGISTRATION].RESEND_SMS,
        method: 'POST' as const,
        description: 'Resend SMS verification code'
    },
    REGISTRATION_COMPLETE: {
        service: SERVICE_NAMES.REGISTRATION,
        path: API_ENDPOINTS[SERVICE_NAMES.REGISTRATION].COMPLETE,
        method: 'POST' as const,
        description: 'Complete registration and get OAuth tokens',
        shouldSetRefreshToken: true
    },
    // User registration endpoints (account-user service, user not authenticated yet)
    ACCOUNT_USER_REGISTRATION: {
        service: SERVICE_NAMES.ACCOUNT_USER,
        path: API_ENDPOINTS[SERVICE_NAMES.ACCOUNT_USER].REGISTRATION,
        method: 'GET' as const,
        description: 'Get user registration by ID'
    },
    ACCOUNT_USER_REGISTRATION_VERIFY: {
        service: SERVICE_NAMES.ACCOUNT_USER,
        path: API_ENDPOINTS[SERVICE_NAMES.ACCOUNT_USER].REGISTRATION,
        method: 'POST' as const,
        description: 'Verify user registration with secret code'
    },
    ACCOUNT_USER_REGISTRATION_CONFIRM: {
        service: SERVICE_NAMES.ACCOUNT_USER,
        path: API_ENDPOINTS[SERVICE_NAMES.ACCOUNT_USER].REGISTRATION_CONFIRM,
        method: 'PUT' as const,
        description: 'Confirm user registration'
    },
    ACCOUNT_USER_REGISTRATION_COMPLETE: {
        service: SERVICE_NAMES.ACCOUNT_USER,
        path: API_ENDPOINTS[SERVICE_NAMES.ACCOUNT_USER].REGISTRATION_COMPLETE,
        method: 'POST' as const,
        description: 'Complete user registration'
    },
    // Two-factor authentication endpoints
    TWO_FACTOR_LOGIN: {
        service: SERVICE_NAMES.TWO_FACTOR,
        path: API_ENDPOINTS[SERVICE_NAMES.TWO_FACTOR].LOGIN,
        method: 'POST' as const,
        description: 'Authenticate user with email and password',
        shouldSetRefreshToken: true,
        shouldForwardTrustedDeviceToken: true
    },
    TWO_FACTOR_VERIFY: {
        service: SERVICE_NAMES.TWO_FACTOR,
        path: API_ENDPOINTS[SERVICE_NAMES.TWO_FACTOR].VERIFY,
        method: 'POST' as const,
        description: 'Verify 2FA challenge with code',
        shouldSetRefreshToken: true,
        shouldSetTrustedDeviceCookie: true
    },
    TWO_FACTOR_RESET_REQUEST: {
        service: SERVICE_NAMES.TWO_FACTOR,
        path: API_ENDPOINTS[SERVICE_NAMES.TWO_FACTOR].RESET_REQUEST,
        method: 'POST' as const,
        description: 'Request 2FA reset - notifies account administrator'
    },
    BRAND_DEFAULT_BRAND: {
        service: SERVICE_NAMES.BRAND,
        path: API_ENDPOINTS[SERVICE_NAMES.BRAND].DEFAULT_BRAND,
        method: 'GET' as const,
        description: 'Get default brand for account in signing context'
    },
    BULK_FAST_TRACK_SENDING: {
        service: SERVICE_NAMES.BULK,
        path: API_ENDPOINTS[SERVICE_NAMES.BULK].FAST_TRACK_SENDING,
        method: 'GET' as const,
        description: 'Get fast track sending info for public page'
    },
    BULK_FAST_TRACK_SIGNING: {
        service: SERVICE_NAMES.BULK,
        path: API_ENDPOINTS[SERVICE_NAMES.BULK].FAST_TRACK_SIGNING,
        method: 'GET' as const,
        description: 'Get fast track signing info for public page'
    },
    BULK_FAST_TRACK_STORE: {
        service: SERVICE_NAMES.BULK,
        path: API_ENDPOINTS[SERVICE_NAMES.BULK].FAST_TRACK_STORE,
        method: 'POST' as const,
        description: 'Submit fast track form from public page'
    }
} as const;

export type S2SEndpointKey = keyof typeof S2S_AUTH_ENDPOINTS;

/**
 * Constructs the client-facing S2S route path.
 *
 * @param key - The S2S endpoint key from S2S_AUTH_ENDPOINTS
 * @returns The full route path for client-side requests (e.g., '/s2s/user/users/security-policy')
 */
export function getS2SRoutePath(key: S2SEndpointKey): string {
    const {service, path} = S2S_AUTH_ENDPOINTS[key];
    return `/s2s/${service.toLowerCase()}/${path}`;
}

/**
 * Constructs the internal proxy path for backend forwarding.
 *
 * @param key - The S2S endpoint key from S2S_AUTH_ENDPOINTS
 * @returns The proxy path for backend service (e.g., '/proxyUser/users/security-policy')
 */
export function getS2SProxyPath(key: S2SEndpointKey): string {
    const {service, path} = S2S_AUTH_ENDPOINTS[key];
    return `${PROXY_PREFIXES[service]}/${path}`;
}

/**
 * Converts a path pattern with :param placeholders to a regex pattern.
 *
 * @param pattern - Path pattern like 'users/:userId/security-policy'
 * @returns Regex pattern that matches actual paths like 'users/123/security-policy'
 */
function getPatternToRegex(pattern: string): RegExp {
    const regexPattern = pattern.replace(/:([a-zA-Z][a-zA-Z0-9]*)/g, '([^/]+)');
    return new RegExp(`^${regexPattern}$`);
}

/**
 * Checks if an actual path matches a pattern with :param placeholders.
 *
 * @param pattern - Path pattern like 'users/:userId/security-policy'
 * @param path - Actual path like 'users/123/security-policy'
 * @returns True if path matches pattern
 */
function getIsPathMatchingPattern(pattern: string, path: string): boolean {
    const regex = getPatternToRegex(pattern);
    return regex.test(path);
}

/**
 * Validates whether a given service and path combination is an allowed S2S endpoint.
 * Supports path patterns with :param placeholders.
 *
 * @param service - The service name to check
 * @param path - The endpoint path to check (can be actual path or pattern)
 * @returns True if the endpoint is in the S2S whitelist
 */
export function getIsS2SEndpointAllowed(service: ServiceName, path: string, method?: string): boolean {
    return Object.values(S2S_AUTH_ENDPOINTS).some(
        (endpoint) => endpoint.service === service
            && (endpoint.path === path || getIsPathMatchingPattern(endpoint.path, path))
            && (!method || endpoint.method === method.toUpperCase())
    );
}

/**
 * Retrieves the S2S endpoint key for a given service and path combination.
 * Supports path patterns with :param placeholders.
 *
 * @param service - The service name to look up
 * @param path - The endpoint path to look up (can be actual path or pattern)
 * @returns The matching S2SEndpointKey or null if not found
 */
export function getS2SEndpointKey(service: ServiceName, path: string, method?: string): S2SEndpointKey | null {
    const entry = Object.entries(S2S_AUTH_ENDPOINTS).find(
        ([, config]) => config.service === service
            && (config.path === path || getIsPathMatchingPattern(config.path, path))
            && (!method || config.method === method.toUpperCase())
    );
    return entry ? entry[0] as S2SEndpointKey : null;
}

/**
 * Constructs the client-facing S2S route path with substituted parameters.
 *
 * @param key - The S2S endpoint key from S2S_AUTH_ENDPOINTS
 * @param params - Object with parameter values to substitute (e.g., {userId: '123'})
 * @returns The full route path with substituted params (e.g., '/s2s/configuration/users/123/security-policy')
 */
export function getS2SRoutePathWithParams(key: S2SEndpointKey, params: Record<string, string>): string {
    const {service, path} = S2S_AUTH_ENDPOINTS[key];
    let substitutedPath: string = path;
    for (const [paramName, paramValue] of Object.entries(params)) {
        substitutedPath = substitutedPath.replace(`:${paramName}`, encodeURIComponent(paramValue));
    }
    return `/s2s/${service.toLowerCase()}/${substitutedPath}`;
}
